import { useRef, type MouseEvent, type RefObject } from 'react';

/**
 * Drag-to-scroll for a horizontally-overflowing strip, mirroring the /events page
 * (Events.jsx): press and drag with the mouse to pan the strip sideways.
 *
 * Wheel is intentionally left to the browser (same as /events). Touch is native.
 * `onClickCapture` swallows the click that ends a real drag so dragging over a
 * chip/button does not activate it — the one addition over the /events version.
 *
 * Snap carousels: snap + `scroll-smooth` fight `scrollLeft` while the mouse is
 * down, so both are disabled for the duration of a drag and restored on release.
 *
 * Spread `handlers` on the scroll container. Pass an existing `ref` when another
 * hook already owns the node; otherwise use the returned `ref`.
 */
export function useDraggableScroll<T extends HTMLElement>(externalRef?: RefObject<T | null>) {
	const innerRef = useRef<T>(null);
	const ref = externalRef ?? innerRef;
	const state = useRef({ isDown: false, startX: 0, startScrollLeft: 0, moved: false });

	const onMouseDown = (e: MouseEvent<T>) => {
		const el = ref.current;
		if (!el) return;
		state.current = {
			isDown: true,
			startX: e.pageX - el.offsetLeft,
			startScrollLeft: el.scrollLeft,
			moved: false,
		};
		el.style.scrollSnapType = 'none';
		el.style.scrollBehavior = 'auto';
	};

	const stop = () => {
		if (!state.current.isDown) return;
		state.current.isDown = false;
		const el = ref.current;
		if (!el) return;
		el.classList.remove('dragging');
		el.style.scrollSnapType = '';
		el.style.scrollBehavior = '';
	};

	const onMouseMove = (e: MouseEvent<T>) => {
		const el = ref.current;
		const s = state.current;
		if (!el || !s.isDown) return;
		e.preventDefault();
		const walk = e.pageX - el.offsetLeft - s.startX;
		if (!s.moved && Math.abs(walk) > 3) {
			s.moved = true;
			el.classList.add('dragging');
		}
		if (s.moved) el.scrollLeft = s.startScrollLeft - walk * 1.5;
	};

	const onClickCapture = (e: MouseEvent<T>) => {
		if (state.current.moved) {
			e.preventDefault();
			e.stopPropagation();
			state.current.moved = false;
		}
	};

	return {
		ref,
		handlers: { onMouseDown, onMouseMove, onMouseUp: stop, onMouseLeave: stop, onClickCapture },
	};
}
